import React, { useState, useEffect, useRef } from "react";
import {
    Modal,
    View,
    Text,
    TouchableOpacity,
    TextInput,
    Image,
    ScrollView,
    Platform,
    ActivityIndicator,
} from "react-native";
import * as Clipboard from "expo-clipboard";
import {
    CreateResponsiveStyle,
    DEVICE_SIZES,
    useDeviceSize,
} from "rn-responsive-styles";
import { LinearGradient } from "expo-linear-gradient";
import { AppColors } from "../../utility/AppColors";
import { AppImages } from "../../utility/AppImages";
import { fetchGenreGames } from "../../services/authAPI";
import { currentEnv } from "../../Api/configs";

interface ApplyAsInfluencerModalProps {
    visible: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    accessToken?: string;
}

interface GameItem {
    title: string;
    [key: string]: any;
}

const countryCodes = [
    { code: "+1", country: "US/CA" },
    { code: "+44", country: "UK" },
    { code: "+91", country: "IN" },
    { code: "+61", country: "AU" },
    { code: "+86", country: "CN" },
    { code: "+81", country: "JP" },
    { code: "+49", country: "DE" },
    { code: "+33", country: "FR" },
    { code: "+39", country: "IT" },
    { code: "+34", country: "ES" },
];

const ApplyAsInfluencerModal: React.FC<ApplyAsInfluencerModalProps> = ({
    visible,
    onClose,
    onSuccess,
    accessToken,
}) => {
    const styles = useStyles();
    const deviceSize = useDeviceSize();
    const isMobile =
        deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

    // Use environment-aware API base URL from configs (removes trailing slash for consistent usage)
    const API_BASE = currentEnv.apiUrl.replace(/\/$/, "");

    // Phone verification state
    const [phoneNumber, setPhoneNumber] = useState("");
    const [countryCode, setCountryCode] = useState("+1");
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
    const [showCountryPicker, setShowCountryPicker] = useState(false);
    const [isPhoneVerified, setIsPhoneVerified] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [savedPhoneNumber, setSavedPhoneNumber] = useState("");
    const [otpCountdown, setOtpCountdown] = useState(0);
    const otpInputRefs = useRef<Array<TextInput | null>>([]);

    // Game selection state
    const [selectedGames, setSelectedGames] = useState<string[]>([]);
    const [gameSearchQuery, setGameSearchQuery] = useState("");
    const [allGames, setAllGames] = useState<GameItem[]>([]);
    const [filteredGames, setFilteredGames] = useState<GameItem[]>([]);
    const [isLoadingGames, setIsLoadingGames] = useState(false);
    const [showGameDropdown, setShowGameDropdown] = useState(false);

    // Social links state
    const [socialLinks, setSocialLinks] = useState({
        twitch: "",
        youtube: "",
        twitter: "",
        tiktok: "",
        instagram: "",
        discord: "",
    });

    // Check phone verification status on modal open
    useEffect(() => {
        if (visible && accessToken) {
            checkPhoneStatus();
            fetchAllGamesFromAPI();
        }
    }, [visible, accessToken]);

    const checkPhoneStatus = async () => {
        setIsCheckingStatus(true);
        try {
            const response = await fetch(
                `${API_BASE}/user/phone-status?accessToken=${accessToken}`
            );
            const data = await response.json();
            console.log("📞 Phone status data received:", data);

            if (data.phoneNumberVerified) {
                setIsPhoneVerified(true);
                if (data.phoneNumber) {
                    setSavedPhoneNumber(data.phoneNumber);
                }
            }

            // Set pre-selected games from backend (regardless of phone verification)
            if (data.selectedGames && Array.isArray(data.selectedGames)) {
                console.log("🎮 Setting selected games:", data.selectedGames);
                setSelectedGames(data.selectedGames);
            }

            // Populate social links from backend (regardless of phone verification)
            if (data.socialLinks && typeof data.socialLinks === 'object') {
                console.log("🔗 Setting social links:", data.socialLinks);
                setSocialLinks({
                    twitch: data.socialLinks.twitchLink || "",
                    youtube: data.socialLinks.youtubeLink || "",
                    twitter: data.socialLinks.xLink || "",
                    tiktok: data.socialLinks.tiktokLink || "",
                    instagram: data.socialLinks.instagramLink || "",
                    discord: data.socialLinks.discordLink || "",
                });
            }
        } catch (error) {
            console.error("Failed to check phone status:", error);
        } finally {
            setIsCheckingStatus(false);
        }
    };

    const fetchAllGamesFromAPI = async () => {
        setIsLoadingGames(true);
        try {
            const response = await fetchGenreGames();
            const responseBody =
                typeof response.body === "string"
                    ? JSON.parse(response.body)
                    : response.body;

            if (response.statusCode === 200 && Array.isArray(responseBody)) {
                setAllGames(responseBody);
                setFilteredGames(responseBody);
            }
        } catch (error) {
            console.error("Failed to fetch games:", error);
        } finally {
            setIsLoadingGames(false);
        }
    };

    useEffect(() => {
        if (gameSearchQuery && allGames.length > 0) {
            const filtered = allGames.filter((game) =>
                game.title.toLowerCase().includes(gameSearchQuery.toLowerCase())
            );
            setFilteredGames(filtered);
            setShowGameDropdown(true);
        } else {
            setFilteredGames(allGames);
            setShowGameDropdown(false);
        }
    }, [gameSearchQuery, allGames]);

    // OTP countdown timer
    useEffect(() => {
        if (otpCountdown > 0) {
            const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [otpCountdown]);

    // Handle OTP digit input
    const handleOtpChange = (text: string, index: number) => {
        const newOtpDigits = [...otpDigits];
        newOtpDigits[index] = text;
        setOtpDigits(newOtpDigits);
        setVerificationCode(newOtpDigits.join(""));

        // Auto-focus next input
        if (text && index < 5) {
            otpInputRefs.current[index + 1]?.focus();
        }
    };

    // Handle backspace
    const handleOtpKeyPress = (e: any, index: number) => {
        if (e.nativeEvent.key === "Backspace" && !otpDigits[index] && index > 0) {
            otpInputRefs.current[index - 1]?.focus();
        }
    };

    // Handle paste from clipboard
    useEffect(() => {
        const handlePaste = async () => {
            // This is primarily for Web, but we can structure it to be reusable
            try {
                let text;
                if (typeof navigator !== "undefined" && navigator.clipboard) {
                    text = await navigator.clipboard.readText();
                } else {
                    text = await Clipboard.getStringAsync();
                }

                if (text && /^\d{6}$/.test(text)) {
                    const digits = text.split("");
                    setOtpDigits(digits);
                    setVerificationCode(text);
                    // Focus last input or blur?
                }
            } catch (error) {
                console.log("Paste error:", error);
            }
        };

        if (typeof window !== "undefined") {
            window.addEventListener("paste", handlePaste);
            return () => {
                window.removeEventListener("paste", handlePaste);
            };
        }
    }, [showOtpInput]); // Re-bind if needed when OTP input shows

    const pasteFromClipboard = async () => {
        try {
            const text = await Clipboard.getStringAsync();
            // Allow 6 digits
            if (text && /^\d{6}$/.test(text)) {
                const digits = text.split("");
                setOtpDigits(digits);
                setVerificationCode(text);
                setErrorMessage("");
            } else if (text && /^\d+$/.test(text) && text.length >= 6) {
                const code = text.substring(0, 6);
                const digits = code.split("");
                setOtpDigits(digits);
                setVerificationCode(code);
                setErrorMessage("");
            } else {
                setErrorMessage("Invalid code in clipboard");
            }
        } catch (error) {
            console.log("Paste from clipboard error:", error);
        }
    };



    const handleSendCode = async () => {
        if (!phoneNumber) {
            setErrorMessage("Please enter a phone number");
            return;
        }

        setIsLoading(true);
        setErrorMessage("");

        try {
            const fullPhoneNumber = countryCode + phoneNumber.replace(/\D/g, '');
            const response = await fetch(`${API_BASE}/user/add-phone`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    accessToken,
                    phoneNumber: fullPhoneNumber,
                }),
            });

            console.log("Response status:", response.status);
            const data = await response.json();
            console.log("Response data:", data);

            if (response.ok && data.success) {
                setIsCodeSent(true);
                setShowOtpInput(true);
                setOtpDigits(["", "", "", "", "", ""]);
                setOtpCountdown(60); // Start 60 second countdown
                console.log("OTP sent successfully");
            } else {
                setErrorMessage(data.error || "Failed to send OTP");
            }
        } catch (error) {
            console.error("Failed to send OTP:", error);
            setErrorMessage("Failed to send OTP. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!verificationCode || verificationCode.length !== 6) {
            setErrorMessage("Please enter a valid 6-digit code");
            return;
        }

        setIsLoading(true);
        setErrorMessage("");

        try {
            const response = await fetch(`${API_BASE}/user/verify-phone`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    accessToken,
                    otp: verificationCode,
                }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setIsPhoneVerified(true);
                setShowOtpInput(false);
                console.log("Phone verified successfully");
            } else {
                setErrorMessage(data.error || "Invalid OTP");
            }
        } catch (error) {
            console.error("Failed to verify OTP:", error);
            setErrorMessage("Failed to verify OTP. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const toggleGameSelection = (gameTitle: string) => {
        if (selectedGames.includes(gameTitle)) {
            setSelectedGames(selectedGames.filter((g) => g !== gameTitle));
        } else {
            setSelectedGames([...selectedGames, gameTitle]);
        }
        setGameSearchQuery("");
        setShowGameDropdown(false);
    };

    // Check if at least one social platform is added
    const hasAnySocialLink = () => {
        return Object.values(socialLinks).some((link) => link.trim() !== "");
    };

    // Check if profile can be completed
    const canCompleteProfile = isPhoneVerified && hasAnySocialLink();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleCompleteProfile = async () => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        setErrorMessage("");

        try {
            // Convert socialLinks to the format expected by the API
            // Format: { twitchLink: "url", youtubeLink: "url", ... }
            const formattedSocialLinks: Record<string, string> = {};

            if (socialLinks.twitch.trim()) {
                formattedSocialLinks.twitchLink = socialLinks.twitch.trim();
            }
            if (socialLinks.youtube.trim()) {
                formattedSocialLinks.youtubeLink = socialLinks.youtube.trim();
            }
            if (socialLinks.twitter.trim()) {
                formattedSocialLinks.xLink = socialLinks.twitter.trim(); // Twitter is stored as xLink
            }
            if (socialLinks.tiktok.trim()) {
                formattedSocialLinks.tiktokLink = socialLinks.tiktok.trim();
            }
            if (socialLinks.instagram.trim()) {
                formattedSocialLinks.instagramLink = socialLinks.instagram.trim();
            }
            if (socialLinks.discord.trim()) {
                formattedSocialLinks.discordLink = socialLinks.discord.trim();
            }

            console.log("=== Submitting Influencer Application ===");
            console.log("Selected Games:", selectedGames);
            console.log("Social Links:", formattedSocialLinks);

            const response = await fetch(`${API_BASE}/user/submit-influencer-application`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    accessToken,
                    selectedGames,
                    socialLinks: formattedSocialLinks,
                }),
            });

            const data = await response.json();

            if (response.ok && data.success) {
                console.log("✅ Influencer application submitted successfully:", data);
                // Call onSuccess to refresh the parent component's status
                if (onSuccess) {
                    onSuccess();
                }
                onClose();
            } else {
                setErrorMessage(data.error || "Failed to submit application");
            }
        } catch (error) {
            console.error("Failed to submit influencer application:", error);
            setErrorMessage("Failed to submit application. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal transparent={true} visible={visible} animationType="slide">
            <View style={styles.overlay}>
                <View style={styles.container}>
                    {/* Gradient Background */}
                    <LinearGradient
                        colors={["#111827", "#000000"]}
                        start={[0, 0]}
                        end={[1, 1]}
                        style={styles.gradientBackground}
                    />

                    {/* Header */}
                    <View style={styles.header}>
                        <Text style={styles.title}>Level up your profile.</Text>
                        <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                            <Image
                                source={AppImages.icCancel}
                                style={styles.closeIcon}
                                resizeMode="contain"
                            />
                        </TouchableOpacity>
                    </View>

                    <ScrollView
                        style={styles.scrollView}
                        showsVerticalScrollIndicator={false}
                        contentContainerStyle={styles.scrollContent}
                    >
                        {/* Description */}
                        <Text style={styles.description}>
                            You're almost there! Finish your profile to unlock creator access,
                            start hosting events, and claim your Founder Pack with 200 KO
                            Tokens.
                        </Text>

                        {/* Phone Verification Section */}
                        <View style={[styles.section, showCountryPicker && { zIndex: 9999 }]}>
                            <Text style={styles.sectionTitle}>
                                Verify your phone number
                                {isPhoneVerified && " ✓"}
                            </Text>

                            {/* Checking Status Indicator */}
                            {isCheckingStatus && (
                                <Text style={styles.checkingText}>Checking verification status...</Text>
                            )}

                            {/* Error Message */}
                            {errorMessage ? (
                                <Text style={styles.errorText}>{errorMessage}</Text>
                            ) : null}

                            {/* Phone Verified State */}
                            {isPhoneVerified ? (
                                <>
                                    <Text style={styles.inputLabel}>Phone Number *</Text>
                                    <View style={styles.phoneInputRow}>
                                        <TextInput
                                            style={[styles.phoneInput, { flex: 1 }]}
                                            value={savedPhoneNumber}
                                            editable={false}
                                        />
                                        <View style={styles.verifiedButton}>
                                            <Text style={styles.verifiedButtonText}>✓ Verified</Text>
                                        </View>
                                    </View>
                                </>
                            ) : !showOtpInput ? (
                                /* Phone Input */
                                <>
                                    <Text style={styles.inputLabel}>Phone Number *</Text>
                                    <View style={[styles.phoneInputRow, isMobile && { flexWrap: 'wrap' }]}>
                                        <TouchableOpacity
                                            style={styles.countryCodePicker}
                                            onPress={() => setShowCountryPicker(!showCountryPicker)}
                                        >
                                            <Text style={styles.countryCodeText}>{countryCode}</Text>
                                            <Image
                                                source={AppImages.arrowDown}
                                                style={styles.dropdownArrow}
                                                resizeMode="contain"
                                            />
                                        </TouchableOpacity>

                                        <TextInput
                                            style={[styles.phoneInput, isMobile && { minWidth: '60%' }]}
                                            placeholder="34 434 4353"
                                            placeholderTextColor="#6B7280"
                                            value={phoneNumber}
                                            onChangeText={setPhoneNumber}
                                            keyboardType="phone-pad"
                                            editable={!isCheckingStatus}
                                        />

                                        <TouchableOpacity
                                            style={[styles.sendCodeButton, isMobile && { marginTop: 10, width: '100%' }]}
                                            onPress={handleSendCode}
                                            disabled={isLoading || isCheckingStatus}
                                        >
                                            <LinearGradient
                                                colors={["#DC2626", "#991B1B"]}
                                                start={[0, 0]}
                                                end={[1, 0]}
                                                style={styles.gradientButton}
                                            />
                                            <Text style={styles.sendCodeText}>
                                                {isLoading ? "Sending..." : isCodeSent ? "Resend" : "Send code"}
                                            </Text>
                                        </TouchableOpacity>
                                    </View>

                                    {showCountryPicker && (
                                        <View style={styles.countryPickerDropdown}>
                                            <ScrollView style={{ maxHeight: 150 }}>
                                                {countryCodes.map((item) => (
                                                    <TouchableOpacity
                                                        key={item.code}
                                                        style={styles.countryPickerItem}
                                                        onPress={() => {
                                                            setCountryCode(item.code);
                                                            setShowCountryPicker(false);
                                                        }}
                                                    >
                                                        <Text style={styles.countryPickerText}>
                                                            {item.code} ({item.country})
                                                        </Text>
                                                    </TouchableOpacity>
                                                ))}
                                            </ScrollView>
                                        </View>
                                    )}
                                </>
                            ) : (
                                /* OTP Input */
                                <>
                                    <Text style={styles.inputLabel}>Enter OTP *</Text>
                                    <View style={styles.otpRow}>
                                        <View style={styles.otpBoxesContainer}>
                                            {otpDigits.map((digit, index) => (
                                                <TextInput
                                                    key={index}
                                                    ref={(ref) => (otpInputRefs.current[index] = ref)}
                                                    style={styles.otpBox}
                                                    value={digit}
                                                    onChangeText={(text) => handleOtpChange(text.slice(-1), index)}
                                                    onKeyPress={(e) => handleOtpKeyPress(e, index)}
                                                    keyboardType="number-pad"
                                                    maxLength={1}
                                                    selectTextOnFocus
                                                />
                                            ))}
                                        </View>

                                        <TouchableOpacity
                                            style={[styles.verifyButton, styles.verifyButtonRed]}
                                            onPress={handleVerifyOtp}
                                            disabled={isLoading || verificationCode.length !== 6}
                                        >
                                            <LinearGradient
                                                colors={["#DC2626", "#991B1B"]}
                                                start={[0, 0]}
                                                end={[1, 0]}
                                                style={styles.gradientButton}
                                            />
                                            <Text style={styles.verifyButtonText}>
                                                {isLoading ? "..." : "Verify"}
                                            </Text>
                                        </TouchableOpacity>
                                    </View>

                                    {/* Paste Button for Mobile */}
                                    {Platform.OS !== 'web' && (
                                        <TouchableOpacity
                                            onPress={pasteFromClipboard}
                                            style={{ alignSelf: 'center', marginTop: 10, marginBottom: 5 }}
                                        >
                                            <Text style={{ color: '#0A89AD', textDecorationLine: 'underline', fontSize: 14 }}>
                                                Paste from clipboard
                                            </Text>
                                        </TouchableOpacity>
                                    )}

                                    <Text style={styles.otpTimerText}>
                                        {otpCountdown > 0
                                            ? `OTP will expire in : ${otpCountdown}`
                                            : "OTP expired. Please resend."}
                                    </Text>
                                    {otpCountdown === 0 && (
                                        <TouchableOpacity onPress={handleSendCode} disabled={isLoading}>
                                            <Text style={styles.resendLink}>Resend OTP</Text>
                                        </TouchableOpacity>
                                    )}
                                </>
                            )}
                        </View>

                        {/* Choose Games Section */}
                        <View style={[styles.section, !isPhoneVerified && styles.disabledSection, { zIndex: 100 }]} pointerEvents={isPhoneVerified ? "auto" : "none"}>
                            <Text style={styles.sectionTitle}>Choose your favourite games</Text>

                            {/* Search Input with Dropdown */}
                            <View style={styles.searchRow}>
                                <TextInput
                                    style={styles.searchInput}
                                    placeholder="Search games..."
                                    placeholderTextColor="#6B7280"
                                    value={gameSearchQuery}
                                    onChangeText={setGameSearchQuery}
                                    editable={isPhoneVerified && !isLoadingGames}
                                    onFocus={() => gameSearchQuery && setShowGameDropdown(true)}
                                />
                                <TouchableOpacity
                                    style={styles.searchButton}
                                    disabled={!isPhoneVerified}
                                    onPress={() => setShowGameDropdown(!showGameDropdown)}
                                >
                                    <Text style={styles.searchButtonText}>
                                        {isLoadingGames ? "Loading..." : "Search"}
                                    </Text>
                                </TouchableOpacity>
                            </View>

                            {/* Dropdown List */}
                            {showGameDropdown && filteredGames.length > 0 && (
                                <View style={styles.gameDropdown}>
                                    <ScrollView style={{ maxHeight: 150 }} nestedScrollEnabled>
                                        {filteredGames.map((game) => {
                                            const isSelected = selectedGames.includes(game.title);
                                            return (
                                                <TouchableOpacity
                                                    key={game.title}
                                                    style={[
                                                        styles.gameDropdownItem,
                                                        isSelected && styles.gameDropdownItemSelected,
                                                    ]}
                                                    onPress={() => toggleGameSelection(game.title)}
                                                >
                                                    <Text style={styles.gameDropdownText}>
                                                        {isSelected ? "✓ " : ""}{game.title}
                                                    </Text>
                                                </TouchableOpacity>
                                            );
                                        })}
                                    </ScrollView>
                                </View>
                            )}

                            {/* Loading Indicator */}
                            {isLoadingGames && (
                                <View style={styles.gamesLoadingContainer}>
                                    <ActivityIndicator size="small" color="#EF4444" />
                                    <Text style={styles.gamesLoadingText}>Loading games...</Text>
                                </View>
                            )}

                            {/* Games Chips Section - Scrollable */}
                            {!isLoadingGames && (
                                <>
                                    {/* Scrollable games container - Vertical */}
                                    <ScrollView
                                        showsVerticalScrollIndicator={true}
                                        style={styles.gamesScrollContainer}
                                        contentContainerStyle={styles.gamesScrollContent}
                                        nestedScrollEnabled
                                    >
                                        {/* Show all selected games as checked chips */}
                                        {selectedGames.map((gameTitle) => (
                                            <TouchableOpacity
                                                key={gameTitle}
                                                style={[styles.gameChip, styles.gameChipSelected]}
                                                onPress={() => toggleGameSelection(gameTitle)}
                                            >
                                                <Image
                                                    source={AppImages.icCheckboxSelected}
                                                    style={styles.checkboxIcon}
                                                    resizeMode="contain"
                                                />
                                                <Text style={styles.gameChipText}>{gameTitle}</Text>
                                            </TouchableOpacity>
                                        ))}

                                        {/* Fill with unselected games up to 8 total (only if selected < 8) */}
                                        {selectedGames.length < 8 && allGames
                                            .filter((game) => !selectedGames.includes(game.title))
                                            .slice(0, 8 - selectedGames.length)
                                            .map((game) => (
                                                <TouchableOpacity
                                                    key={game.title}
                                                    style={styles.gameChip}
                                                    onPress={() => toggleGameSelection(game.title)}
                                                >
                                                    <Image
                                                        source={AppImages.icCheckboxNotSelected}
                                                        style={styles.checkboxIcon}
                                                        resizeMode="contain"
                                                    />
                                                    <Text style={styles.gameChipText}>{game.title}</Text>
                                                </TouchableOpacity>
                                            ))}
                                    </ScrollView>
                                </>
                            )}
                        </View>

                        {/* Social Links Section */}
                        <View style={[styles.section, !isPhoneVerified && styles.disabledSection]} pointerEvents={isPhoneVerified ? "auto" : "none"}>
                            <Text style={styles.sectionTitle}>Link your social accounts</Text>
                            <View style={styles.socialGrid}>
                                <View style={styles.socialInputWrapper}>
                                    <Text style={styles.socialLabel}>Twitch</Text>
                                    <TextInput
                                        style={styles.socialInput}
                                        placeholder="your_twitch_username"
                                        placeholderTextColor="#6B7280"
                                        value={socialLinks.twitch}
                                        onChangeText={(text) =>
                                            setSocialLinks({ ...socialLinks, twitch: text })
                                        }
                                    />
                                </View>
                                <View style={styles.socialInputWrapper}>
                                    <Text style={styles.socialLabel}>YouTube</Text>
                                    <TextInput
                                        style={styles.socialInput}
                                        placeholder="your_channel_name"
                                        placeholderTextColor="#6B7280"
                                        value={socialLinks.youtube}
                                        onChangeText={(text) =>
                                            setSocialLinks({ ...socialLinks, youtube: text })
                                        }
                                    />
                                </View>
                                <View style={styles.socialInputWrapper}>
                                    <Text style={styles.socialLabel}>Twitter/X</Text>
                                    <TextInput
                                        style={styles.socialInput}
                                        placeholder="@your_handle"
                                        placeholderTextColor="#6B7280"
                                        value={socialLinks.twitter}
                                        onChangeText={(text) =>
                                            setSocialLinks({ ...socialLinks, twitter: text })
                                        }
                                    />
                                </View>
                                <View style={styles.socialInputWrapper}>
                                    <Text style={styles.socialLabel}>TikTok</Text>
                                    <TextInput
                                        style={styles.socialInput}
                                        placeholder="@your_handle"
                                        placeholderTextColor="#6B7280"
                                        value={socialLinks.tiktok}
                                        onChangeText={(text) =>
                                            setSocialLinks({ ...socialLinks, tiktok: text })
                                        }
                                    />
                                </View>
                                <View style={styles.socialInputWrapper}>
                                    <Text style={styles.socialLabel}>Instagram</Text>
                                    <TextInput
                                        style={styles.socialInput}
                                        placeholder="@your_handle"
                                        placeholderTextColor="#6B7280"
                                        value={socialLinks.instagram}
                                        onChangeText={(text) =>
                                            setSocialLinks({ ...socialLinks, instagram: text })
                                        }
                                    />
                                </View>
                                <View style={styles.socialInputWrapper}>
                                    <Text style={styles.socialLabel}>Discord</Text>
                                    <TextInput
                                        style={styles.socialInput}
                                        placeholder="@your_handle"
                                        placeholderTextColor="#6B7280"
                                        value={socialLinks.discord}
                                        onChangeText={(text) =>
                                            setSocialLinks({ ...socialLinks, discord: text })
                                        }
                                    />
                                </View>
                            </View>
                        </View>
                    </ScrollView>

                    {/* Footer Buttons */}
                    <View style={styles.footer}>
                        <TouchableOpacity style={styles.maybeLaterButton} onPress={onClose} disabled={isSubmitting}>
                            <Text style={styles.maybeLaterText}>Maybe Later</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={[styles.completeButton, (!canCompleteProfile || isSubmitting) && styles.disabledSection]}
                            onPress={handleCompleteProfile}
                            disabled={!canCompleteProfile || isSubmitting}
                        >
                            <LinearGradient
                                colors={["#DC2626", "#991B1B"]}
                                start={[0, 0]}
                                end={[1, 0]}
                                style={styles.completeGradient}
                            />
                            <Text style={styles.completeButtonText}>
                                {isSubmitting ? "Submitting..." : "Complete My Profile"}
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        </Modal>
    );
};

const useStyles = CreateResponsiveStyle(
    {
        overlay: {
            flex: 1,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            justifyContent: "center",
            alignItems: "center",
        },
        container: {
            width: "90%",
            maxWidth: 577,
            maxHeight: "90%",
            borderRadius: 8,
            borderWidth: 1.6,
            borderColor: "rgba(239, 68, 68, 0.50)",
            overflow: "hidden",
        },
        gradientBackground: {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
        },
        header: {
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 20,
            paddingBottom: 0,
        },
        title: {
            color: "#FFF",
            fontSize: 22,
            fontWeight: "700",
            lineHeight: 31,
        },
        closeButton: {
            backgroundColor: "rgba(75, 85, 99, 0.5)",
            width: 32,
            height: 32,
            borderRadius: 16,
            justifyContent: "center",
            alignItems: "center",
        },
        closeIcon: {
            width: 16,
            height: 16,
            tintColor: AppColors.white,
        },
        scrollView: {
            flex: 1,
        },
        scrollContent: {
            padding: 20,
            paddingTop: 16,
        },
        description: {
            color: "#FFF",
            fontSize: 18,
            fontWeight: "500",
            lineHeight: 25,
            marginBottom: 20,
        },
        section: {
            marginBottom: 20,
            position: "relative",
            zIndex: 1,
        },
        sectionTitle: {
            color: "#FFF",
            fontSize: 18,
            fontWeight: "500",
            lineHeight: 30,
            marginBottom: 8,
        },
        inputLabel: {
            color: "#FFF",
            fontSize: 14,
            fontWeight: "500",
            marginBottom: 8,
        },
        phoneInputRow: {
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
        },
        countryCodePicker: {
            height: 49,
            paddingHorizontal: 12,
            borderRadius: 8,
            borderWidth: 0.8,
            borderColor: "#374151",
            backgroundColor: "#1F2937",
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
        },
        countryCodeText: {
            color: "#FFF",
            fontSize: 14,
        },
        dropdownArrow: {
            width: 12,
            height: 12,
            tintColor: "#FFF",
        },
        phoneInput: {
            flex: 1,
            height: 49,
            borderRadius: 8,
            borderWidth: 0.8,
            borderColor: "#374151",
            backgroundColor: "#1F2937",
            paddingHorizontal: 12,
            color: "#FFF",
            fontSize: 14,
        },
        sendCodeButton: {
            height: 49,
            paddingHorizontal: 20,
            borderRadius: 6,
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
        },
        gradientButton: {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
        },
        sendCodeText: {
            color: "#FFF",
            fontSize: 14,
            fontWeight: "700",
        },
        countryPickerDropdown: {
            position: "absolute",
            top: 90,
            left: 0,
            width: 150,
            backgroundColor: "#1F2937",
            borderRadius: 8,
            borderWidth: 0.8,
            borderColor: "#374151",
            zIndex: 9999,
            elevation: 10,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
        },
        countryPickerItem: {
            padding: 12,
            borderBottomWidth: 0.5,
            borderBottomColor: "#374151",
        },
        countryPickerText: {
            color: "#FFF",
            fontSize: 14,
        },
        searchRow: {
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
        },
        searchInput: {
            flex: 1,
            height: 48,
            borderRadius: 8,
            borderWidth: 0.8,
            borderColor: "#374151",
            backgroundColor: "#1F2937",
            paddingHorizontal: 12,
            color: "#FFF",
            fontSize: 14,
        },
        searchButton: {
            height: 48,
            paddingHorizontal: 24,
            borderRadius: 6,
            borderWidth: 1,
            borderColor: "#FFF",
            justifyContent: "center",
            alignItems: "center",
        },
        searchButtonText: {
            color: "#FFF",
            fontSize: 14,
            fontWeight: "600",
        },
        gamesScrollContainer: {
            maxHeight: 100,
            marginTop: 8,
        },
        gamesScrollContent: {
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 8,
            paddingBottom: 4,
        },
        gamesChipContainer: {
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 8,
        },
        gameChip: {
            flexDirection: "row",
            alignItems: "center",
            height: 34,
            paddingHorizontal: 8,
            paddingVertical: 9,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: "#374151",
            backgroundColor: "#1F2937",
            gap: 8,
        },
        gameChipSelected: {
            borderColor: "#EF4444",
        },
        checkboxIcon: {
            width: 18,
            height: 18,
        },
        gameChipText: {
            color: "#FFF",
            fontSize: 16,
            fontWeight: "400",
            lineHeight: 18,
        },
        // Dropdown styles
        gameDropdown: {
            position: "absolute",
            top: 60,
            left: 0,
            right: 0,
            backgroundColor: "#1F2937",
            borderRadius: 8,
            borderWidth: 0.8,
            borderColor: "#374151",
            zIndex: 9999,
            elevation: 10,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.3,
            shadowRadius: 8,
        },
        gameDropdownItem: {
            padding: 12,
            borderBottomWidth: 0.5,
            borderBottomColor: "#374151",
        },
        gameDropdownItemSelected: {
            backgroundColor: "rgba(239, 68, 68, 0.1)",
        },
        gameDropdownText: {
            color: "#FFF",
            fontSize: 14,
        },
        gamesLoadingContainer: {
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            paddingVertical: 16,
        },
        gamesLoadingText: {
            color: "#9CA3AF",
            fontSize: 14,
        },
        noGamesText: {
            color: "#9CA3AF",
            fontSize: 14,
            textAlign: "center",
            paddingVertical: 16,
        },
        socialGrid: {
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 12,
        },
        socialInputWrapper: {
            width: "48%",
        },
        socialLabel: {
            color: "#9CA3AF",
            fontSize: 12,
            marginBottom: 4,
        },
        socialInput: {
            height: 42,
            borderRadius: 8,
            borderWidth: 0.8,
            borderColor: "#374151",
            backgroundColor: "#1F2937",
            paddingHorizontal: 8,
            color: "#FFF",
            fontSize: 14,
        },
        checkingText: {
            color: "#FBBF24", // Yellow color
            fontSize: 14,
            marginBottom: 8,
        },
        footer: {
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            padding: 20,
            paddingTop: 16,
            borderTopWidth: 0.8,
            borderTopColor: "#374151",
        },
        maybeLaterButton: {
            height: 44,
            paddingHorizontal: 20,
            borderRadius: 6,
            borderWidth: 1,
            borderColor: "#FFF",
            justifyContent: "center",
            alignItems: "center",
        },
        maybeLaterText: {
            color: "#FFF",
            fontSize: 14,
            fontWeight: "600",
        },
        completeButton: {
            height: 44,
            paddingHorizontal: 24,
            borderRadius: 6,
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
        },
        completeGradient: {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
        },
        completeButtonText: {
            color: "#FFF",
            fontSize: 14,
            fontWeight: "700",
        },
        // OTP verification styles
        errorText: {
            color: "#EF4444",
            fontSize: 14,
            marginBottom: 8,
        },
        verifiedContainer: {
            padding: 12,
            backgroundColor: "rgba(34, 197, 94, 0.1)",
            borderRadius: 8,
            borderWidth: 1,
            borderColor: "rgba(34, 197, 94, 0.3)",
        },
        verifiedText: {
            color: "#22C55E",
            fontSize: 14,
            fontWeight: "500",
        },
        otpRow: {
            flexDirection: "row",
            alignItems: "center",
            gap: 12,
        },
        otpInput: {
            flex: 1,
            height: 52,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: "#374151",
            backgroundColor: "#1F2937",
            paddingHorizontal: 16,
            color: "#FFF",
            fontSize: 18,
            letterSpacing: 4,
            textAlign: "center",
        },
        otpBoxesContainer: {
            flexDirection: "row",
            gap: 8,
            flex: 1,
        },
        otpBox: {
            width: 48,
            height: 52,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: "#374151",
            backgroundColor: "#1F2937",
            color: "#FFF",
            fontSize: 20,
            fontWeight: "600",
            textAlign: "center",
        },
        otpTimerText: {
            color: "#9CA3AF",
            fontSize: 14,
            textAlign: "center",
            marginTop: 12,
        },
        verifyButtonRed: {
            overflow: "hidden",
        },
        verifyButton: {
            height: 52,
            paddingHorizontal: 24,
            borderRadius: 8,
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
        },
        verifyButtonText: {
            color: "#FFF",
            fontSize: 14,
            fontWeight: "600",
        },
        resendLink: {
            color: "#9CA3AF",
            fontSize: 14,
            textDecorationLine: "underline",
            marginTop: 12,
        },
        verifiedButton: {
            height: 52,
            paddingHorizontal: 24,
            borderRadius: 8,
            backgroundColor: "#22C55E",
            justifyContent: "center",
            alignItems: "center",
        },
        verifiedButtonText: {
            color: "#FFF",
            fontSize: 14,
            fontWeight: "600",
        },
        disabledSection: {
            opacity: 0.5,
        },
    },
    {
        [DEVICE_SIZES.XS]: {
            overlay: {
                justifyContent: "flex-end",
            },
            container: {
                width: "100%",
                maxWidth: "100%",
                maxHeight: "85%",
                borderRadius: 0,
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                borderBottomWidth: 0,
            },
            title: {
                fontSize: 18,
                lineHeight: 24,
            },
            description: {
                fontSize: 14,
                lineHeight: 20,
            },
            sectionTitle: {
                fontSize: 16,
                lineHeight: 24,
            },
            socialInputWrapper: {
                width: "100%",
            },
            gameChipText: {
                fontSize: 14,
            },
        },
        [DEVICE_SIZES.SM]: {
            overlay: {
                justifyContent: "flex-end",
            },
            container: {
                width: "100%",
                maxWidth: "100%",
                maxHeight: "85%",
                borderRadius: 0,
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                borderBottomWidth: 0,
            },
            title: {
                fontSize: 18,
                lineHeight: 24,
            },
            description: {
                fontSize: 14,
                lineHeight: 20,
            },
            sectionTitle: {
                fontSize: 16,
                lineHeight: 24,
            },
            socialInputWrapper: {
                width: "100%",
            },
        },
    }
);

export default ApplyAsInfluencerModal;
